import { DenormalizedFormat } from '@aipb/gulliver/dropbox/model';
import { Pagination } from '@aipb/gulliver/dynamic-gallery/model';
import { DynamicGalleryRouting } from '@aipb/gulliver/dynamic-gallery/routing';
import { Component, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { Map, Overlay, View } from 'ol';
import { Control } from 'ol/control';
import { Interaction } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import { Observable } from 'rxjs';
import { ImagePreviewItem } from '../../model';
import { ImagePreviewOpenLayersService } from '../../services/image-preview-open-layers.service';

@Component({
  selector: 'ip-open-layers',
  templateUrl: './open-layers.component.html',
  styleUrls: ['./open-layers.component.scss']
})
export class OpenLayersComponent implements OnChanges {
  @Input() id: string;

  @Input() image: string;
  @Input() formats: DenormalizedFormat[] = [];
  @Input() items: ImagePreviewItem[] = [];

  @Input() pagination?: Pagination;
  @Output() detail = new EventEmitter<ImagePreviewItem>();

  public layers$: Observable<BaseLayer[]> = this.dynamicGalleryOpenLayers.layers$;
  public view$: Observable<View> = this.dynamicGalleryOpenLayers.view$;
  public controls$: Observable<Control[]> = this.dynamicGalleryOpenLayers.controls$;
  public interactions$: Observable<Interaction[]> = this.dynamicGalleryOpenLayers.interactions$;
  public overlays$: Observable<Overlay[]> = this.dynamicGalleryOpenLayers.overlays;

  constructor(
    private dynamicGalleryOpenLayers: ImagePreviewOpenLayersService,
    private dynamicGalleryRouting: DynamicGalleryRouting
  ) {}

  @HostListener('document:keydown', ['$event']) onKeydownHandler(evt: KeyboardEvent) {
    switch (evt.code) {
      case 'ArrowLeft':
        if (!this.isPrevDisabled()) {
          this.onPrev();
        }
        break;
      case 'ArrowRight':
        if (!this.isNextDisabled()) {
          this.onNext();
        }
        break;
    }
  }

  onMapReady(map: Map): void {
    this.dynamicGalleryOpenLayers.updateMap(map);
  }

  ngOnChanges() {
    if (!this.image) return;

    this.dynamicGalleryOpenLayers.init(this.image, this.formats);
  }

  isPrevDisabled(): boolean {
    const currentIndex = this.items.findIndex((item) => item.id === this.id);
    const currentPageNum = this.pagination ? this.pagination.pageNum : -1;

    return currentIndex - 1 <= 0 && currentPageNum - 1 < 0;
  }

  isNextDisabled(): boolean {
    const currentIndex = this.items.findIndex((item) => item.id === this.id);
    const currentPageNum = this.pagination ? this.pagination.pageNum : -1;

    return (
      currentIndex + 1 >= this.items.length &&
      currentPageNum + 1 >= (this.pagination ? this.pagination.links.length : 0)
    );
  }

  onPrev(): void {
    const currentIndex = this.items.findIndex((item) => item.id === this.id);
    const prevIndex = currentIndex - 1;

    if (prevIndex < 0) {
      const currentPageLink = this.pagination ? this.pagination.links.find((l) => l.current) : null;
      if (!currentPageLink) return;

      const currentPageIndex = this.pagination.links.findIndex((l) => l.current);
      const prevPageIndex = currentPageIndex - 1;

      if (prevPageIndex >= this.pagination.links.length) return;

      this.dynamicGalleryRouting.default(this.pagination.links[prevPageIndex].url);

      return;
    }

    this.detail.emit(this.items[prevIndex]);
  }

  onNext(): void {
    const currentIndex = this.items.findIndex((item) => item.id === this.id);
    const nextIndex = currentIndex + 1;

    if (nextIndex >= this.items.length) {
      const currentPageLink = this.pagination ? this.pagination.links.find((l) => l.current) : null;
      if (!currentPageLink) return;

      const currentPageIndex = this.pagination.links.findIndex((l) => l.current);
      const nextPageIndex = currentPageIndex + 1;

      if (nextPageIndex >= this.pagination.links.length) return;

      this.dynamicGalleryRouting.default(this.pagination.links[nextPageIndex].url);

      return;
    }

    this.detail.emit(this.items[nextIndex]);
  }
}
